import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CaretLeft, CaretRight, Gauge, Warning, Gavel } from '@phosphor-icons/react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { todayKey } from '@/lib/diaryStore';
import { fetchCoverage, type CoverageBarrister } from '@/lib/operationsApi';
import { QueryErrorState } from '@/components/shared/QueryErrorState';

/**
 * Coverage Board — a PLANNING view for clerks/practice managers ("who has capacity, who is
 * overloaded, where are the gaps?"). Presentation only: consumes GET /operations/coverage and
 * renders a weekly capacity grid + planning indicators. No business logic, no new API.
 */
export default function CoverageBoardPage() {
  const navigate = useNavigate();
  const [monday, setMonday] = useState<string>(() => weekStartOf(todayKey()));

  const from = `${monday}T00:00:00.000Z`;
  const to = `${addDays(monday, 6)}T23:59:59.999Z`;

  const q = useQuery({
    queryKey: ['coverage', monday],
    queryFn: () => fetchCoverage({ scope: 'supported', from, to }),
    staleTime: 60_000,
  });

  const data = q.data;
  const shiftWeek = (delta: number) => setMonday((m) => addDays(m, delta * 7));

  return (
    <div className="space-y-5 px-1 py-2">
      {/* Header + week nav */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Gauge className="h-6 w-6 text-brand-600 dark:text-brand-400" weight="duotone" />
          <div>
            <h1 className="text-xl font-bold tracking-tight text-foreground">Coverage</h1>
            <p className="text-xs text-muted-foreground">Capacity, workload &amp; gaps across your barristers</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => shiftWeek(-1)} className="flex h-8 w-8 items-center justify-center rounded-lg border border-border hover:bg-muted"><CaretLeft className="h-4 w-4" /></button>
          <button onClick={() => setMonday(weekStartOf(todayKey()))} className="rounded-lg border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted">This week</button>
          <button onClick={() => shiftWeek(1)} className="flex h-8 w-8 items-center justify-center rounded-lg border border-border hover:bg-muted"><CaretRight className="h-4 w-4" /></button>
          <span className="ml-2 text-sm font-medium text-foreground">
            {new Date(`${monday}T12:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} – {new Date(`${addDays(monday, 6)}T12:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
          </span>
        </div>
      </div>

      {q.isLoading && <div className="text-sm text-muted-foreground">Loading coverage…</div>}

      {/* The grid below renders only when `data` arrived, so a failed fetch left the page blank
          under its header — no grid, no message, nothing to retry. */}
      {q.isError && (
        <Card className="p-6">
          <QueryErrorState what="the coverage board" onRetry={() => void q.refetch()} />
        </Card>
      )}

      {/* Weekly capacity grid */}
      {data && (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] border-collapse text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30 text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-2 font-semibold">Barrister</th>
                  {data.days.map((d) => (
                    <th key={d} className="px-2 py-2 text-center font-semibold">
                      {new Date(`${d}T12:00:00Z`).toLocaleDateString('en-GB', { weekday: 'short' })}
                      <div className="text-[10px] font-normal opacity-70">{new Date(`${d}T12:00:00Z`).getUTCDate()}</div>
                    </th>
                  ))}
                  <th className="px-3 py-2 text-center font-semibold">Load</th>
                </tr>
              </thead>
              <tbody>
                {data.barristers.map((b) => (
                  <tr key={b.userId} className="border-b border-border/40 last:border-0 hover:bg-muted/20">
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-foreground">{b.name}</div>
                      <div className="mt-0.5 flex flex-wrap gap-1">{indicators(b)}</div>
                    </td>
                    {b.days.map((d) => (
                      <td key={d.date} className="px-2 py-2.5 text-center">
                        {d.onLeave ? (
                          <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-muted text-[9px] font-bold text-muted-foreground" title="On leave">OOO</span>
                        ) : d.hearings > 0 ? (
                          <span className={`inline-flex h-7 w-7 items-center justify-center rounded-md text-xs font-bold tabular-nums ${cellTone(d.hearings)}`} title={`${d.hearings} hearing(s)`}>
                            {d.hearings}
                          </span>
                        ) : (
                          <span className="text-muted-foreground/30">·</span>
                        )}
                      </td>
                    ))}
                    <td className="px-3 py-2.5 text-center">{loadBadge(b)}</td>
                  </tr>
                ))}
                {data.barristers.length === 0 && (
                  <tr><td colSpan={data.days.length + 2} className="px-4 py-10 text-center text-sm text-muted-foreground">No barristers in scope.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Uncovered hearings — reassignment targets */}
      {data && data.uncovered.length > 0 && (
        <Card className="border-l-4 border-l-red-400">
          <CardContent className="space-y-2 p-4">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-red-700 dark:text-red-300">
              <Warning className="h-4 w-4" weight="fill" /> Uncovered hearings — need counsel
            </div>
            {data.uncovered.map((u) => (
              <button key={u.id} onClick={() => navigate(`/clerk/hearings/${u.id}`)} className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-red-700 dark:text-red-300 hover:bg-muted/40">
                <Gavel className="h-4 w-4 shrink-0" />
                <span className="flex-1">{[new Date(`${u.date}T12:00:00Z`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }), u.matter, u.court].filter(Boolean).join(' · ')}</span>
              </button>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* ── Planning indicators (presentation of canonical data) ── */
function indicators(b: CoverageBarrister) {
  const chips: React.ReactNode[] = [];
  if (b.load === 'light' && b.totalHearings === 0) chips.push(<Badge key="spare" variant="success">Spare capacity</Badge>);
  else if (b.load === 'light') chips.push(<Badge key="under" variant="success">Under-utilised</Badge>);
  if (b.leaveDays.length > 0) chips.push(<Badge key="leave" variant="outline">On leave {b.leaveDays.length}d</Badge>);
  if (b.prepGaps > 0) chips.push(<Badge key="prep" variant="warning">{b.prepGaps} prep gap{b.prepGaps === 1 ? '' : 's'}</Badge>);
  if (b.load === 'overloaded') chips.push(<Badge key="reassign" variant="danger">Reassign?</Badge>);
  return chips;
}

function loadBadge(b: CoverageBarrister) {
  if (b.load === 'overloaded') return <Badge variant="danger">Overloaded</Badge>;
  if (b.load === 'busy') return <Badge variant="warning">Busy</Badge>;
  return <Badge variant="outline">{b.totalHearings === 0 ? 'Free' : 'Light'}</Badge>;
}

function cellTone(n: number): string {
  if (n >= 3) return 'bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300';
  if (n === 2) return 'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300';
  return 'bg-brand-50 text-brand-700 dark:text-brand-300';
}

/* ── date helpers (business-tz week, Monday start) ── */
function weekStartOf(dayKey: string): string {
  const d = new Date(`${dayKey}T12:00:00Z`);
  const dow = (d.getUTCDay() + 6) % 7; // 0 = Monday
  d.setUTCDate(d.getUTCDate() - dow);
  return d.toISOString().slice(0, 10);
}
function addDays(dayKey: string, n: number): string {
  const d = new Date(`${dayKey}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
